import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import DashboardHeader from "../components/DashboardHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, PiggyBank, Coins, Receipt, ArrowRightLeft, HandCoins } from "lucide-react";

const isExpensesDesc = (d) => ["despesas", "despesa"].includes(d.trim().toLowerCase());

const Toggle = ({ value, onChange, testid }) => (
  <div className="inline-flex rounded-md border border-border bg-white p-1 h-10" data-testid={testid}>
    {["CVE", "EUR"].map((c) => (
      <button key={c} type="button" onClick={() => onChange(c)} data-testid={`${testid}-${c.toLowerCase()}`}
        className={`px-3 rounded text-sm font-semibold transition-colors ${value === c ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground"}`}>
        {c === "EUR" ? "€" : "CVE"}
      </button>
    ))}
  </div>
);

const makeFmt = (display, rate) => (cve) => {
  const amt = Number(cve || 0);
  if (display === "EUR") return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(amt / (rate || 1));
  return `${new Intl.NumberFormat("pt-PT", { maximumFractionDigits: 0 }).format(Math.round(amt))} CVE`;
};

function EntryDialog({ open, kind, onClose, onSaved, defaultRate, expensesTotal, fmt }) {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("CVE");
  const [rate, setRate] = useState(defaultRate);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setDescription(""); setAmount(""); setCurrency("CVE"); setRate(defaultRate); } }, [open, defaultRate]);
  const auto = kind === "allocation" && isExpensesDesc(description);
  const preview = auto ? expensesTotal : (Number(amount) || 0) * (currency === "EUR" ? Number(rate) || 0 : 1);

  const submit = async (e) => {
    e.preventDefault();
    if (!description.trim()) { toast.error("Escreve a descrição"); return; }
    if (!auto && !(Number(amount) > 0)) { toast.error("Valor inválido"); return; }
    setSaving(true);
    try {
      await api.post("/money-split/entries", { kind, description: description.trim(), amount: Number(amount) || 0, currency, rate: currency === "EUR" ? Number(rate) : null });
      toast.success(kind === "investment" ? "Investimento adicionado" : "Divisão adicionada");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao guardar"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="money-entry-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{kind === "investment" ? "Valor investido" : "Nova divisão"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Descrição</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="money-entry-description"
              placeholder={kind === "investment" ? "Ex: Investimento inicial" : "Ex: Comprar produtos, Lucro Adelaide, Despesas"} />
            {kind === "allocation" && <p className="text-[11px] text-muted-foreground">Escreve “Despesas” para descontar automaticamente o total das despesas registadas.</p>}
          </div>
          {auto ? (
            <div className="rounded-xl bg-[hsl(var(--secondary))] p-3 text-sm" data-testid="money-entry-expenses-auto">
              Total das despesas: <b>{fmt(expensesTotal)}</b> (atualiza sozinho)
            </div>
          ) : (
            <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs">Valor</Label>
                <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" data-testid="money-entry-amount" />
              </div>
              <Toggle value={currency} onChange={setCurrency} testid="money-entry-currency" />
            </div>
          )}
          {!auto && currency === "EUR" && (
            <div className="space-y-1">
              <Label className="text-xs">Taxa de câmbio desta operação (CVE por 1 €)</Label>
              <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="money-entry-rate" />
            </div>
          )}
          <div className="text-sm text-muted-foreground" data-testid="money-entry-preview">Equivale a <b className="text-foreground">{Math.round(preview).toLocaleString("pt-PT")} CVE</b></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58] text-white w-full" data-testid="money-entry-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCards({ data, fmt }) {
  const Card = ({ label, value, icon, testid, tone = "" }) => (
    <div className="rounded-2xl bg-white lb-shadow-card p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">{icon} {label}</div>
      <div className={`mt-1 font-display text-xl sm:text-2xl ${tone}`} data-testid={testid}>{value}</div>
    </div>
  );
  return (
    <>
      <div className="rounded-3xl bg-[hsl(var(--primary))] text-white p-6 lb-shadow-card lb-fade-in">
        <div className="text-xs uppercase tracking-widest opacity-80">Dinheiro por dividir</div>
        <div className="font-display text-4xl sm:text-5xl mt-2" data-testid="money-available">{fmt(data.available)}</div>
        <div className="text-xs opacity-80 mt-2">Total {fmt(data.total)} − dividido {fmt(data.allocated)}</div>
        {data.allocated > data.total + 0.01 && (
          <div className="mt-3 text-xs bg-white/20 rounded-lg px-3 py-2" data-testid="money-over-allocated-warning">
            Atenção: dividiste {fmt(data.allocated - data.total)} a mais do que o dinheiro total (ex.: novas despesas).
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card label="Recebido (vendas)" value={fmt(data.received)} icon={<Coins className="w-3 h-3" />} testid="money-received" />
        <Card label="Investido" value={fmt(data.invested)} icon={<PiggyBank className="w-3 h-3" />} testid="money-invested" />
        <Card label="Já dividido" value={fmt(data.allocated)} icon={<ArrowRightLeft className="w-3 h-3" />} testid="money-allocated" />
        <Card label="Despesas registadas" value={fmt(data.expenses_total)} icon={<Receipt className="w-3 h-3" />} testid="money-expenses" tone="text-[hsl(var(--destructive))]" />
      </div>
    </>
  );
}

function EntryRow({ e, fmt, onDelete }) {
  const inv = e.kind === "investment";
  return (
    <div className="bg-white rounded-xl lb-shadow-card p-3 flex items-center gap-3" data-testid={`money-entry-row-${e.id}`}>
      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${inv ? "bg-emerald-100 text-emerald-700" : "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]"}`}>
        {inv ? <PiggyBank className="w-4 h-4" /> : e.is_expenses ? <Receipt className="w-4 h-4" /> : <HandCoins className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{e.description}</div>
        <div className="text-xs text-muted-foreground">
          {new Date(e.created_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" })}
          {e.input_currency === "EUR" && ` · ${Number(e.input_amount).toLocaleString("pt-PT")} € × ${e.rate}`}
          {e.is_expenses && " · automático"}
        </div>
      </div>
      <div className={`font-display text-lg ${inv ? "text-emerald-700" : ""}`}>{inv ? "+" : "−"} {fmt(e.amount_cve)}</div>
      <Button variant="ghost" size="icon" onClick={() => onDelete(e.id)} data-testid={`delete-money-entry-${e.id}`}>
        <Trash2 className="w-4 h-4 text-muted-foreground" />
      </Button>
    </div>
  );
}

export default function MoneySplit() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const { currency: display } = useCurrency();
  const [rateInput, setRateInput] = useState("");
  const [dialogKind, setDialogKind] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/money-split");
      setData(data);
      setRateInput((r) => r || String(data.default_rate));
    } catch { toast.error("Erro ao carregar"); }
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar…</div>;
  if (user === null) return <Navigate to="/login" replace />;

  const saveRate = async () => {
    if (!(Number(rateInput) > 0)) { toast.error("Taxa inválida"); return; }
    try { await api.put("/money-split/rate", { rate: Number(rateInput) }); toast.success("Taxa guardada"); load(); }
    catch { toast.error("Erro ao guardar taxa"); }
  };
  const doDelete = async () => {
    try { await api.delete(`/money-split/entries/${deleteId}`); toast.success("Eliminado"); setDeleteId(null); load(); }
    catch { toast.error("Erro ao eliminar"); }
  };
  const fmt = makeFmt(display, data?.default_rate);

  return (
    <div className="min-h-screen bg-background" data-testid="money-split-page">
      <DashboardHeader title="Divisão de dinheiro" />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3 lb-fade-in">
          <div>
            <div className="lb-overline">Caixa</div>
            <h1 className="font-display text-3xl sm:text-4xl mt-1">Divisão de dinheiro</h1>
            <p className="text-sm text-muted-foreground mt-1">Dinheiro recebido (vendas a dinheiro + fiados já pagos) e para onde foi.</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl lb-shadow-card p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1 flex-1 min-w-[180px]">
            <Label className="text-xs">Taxa de câmbio desta página (CVE por 1 €)</Label>
            <Input type="number" step="0.01" value={rateInput} onChange={(e) => setRateInput(e.target.value)} data-testid="money-split-rate-input" />
          </div>
          <Button variant="outline" onClick={saveRate} data-testid="money-split-rate-save">Guardar taxa</Button>
        </div>

        {!data ? <div className="h-40 bg-white rounded-3xl lb-shadow-card animate-pulse" /> : (
          <>
            <SummaryCards data={data} fmt={fmt} />
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setDialogKind("allocation")} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58] text-white rounded-full" data-testid="add-allocation-btn">
                <Plus className="w-4 h-4 mr-1" /> Acrescentar divisão
              </Button>
              <Button variant="outline" onClick={() => setDialogKind("investment")} className="rounded-full" data-testid="add-investment-btn">
                <PiggyBank className="w-4 h-4 mr-1" /> Acrescentar valor investido
              </Button>
            </div>
            {data.entries.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl lb-shadow-card text-sm text-muted-foreground" data-testid="empty-money-entries">Ainda sem divisões registadas.</div>
            ) : (
              <div className="space-y-2">{data.entries.map((e) => <EntryRow key={e.id} e={e} fmt={fmt} onDelete={setDeleteId} />)}</div>
            )}
          </>
        )}
      </main>

      <EntryDialog open={!!dialogKind} kind={dialogKind} onClose={() => setDialogKind(null)} onSaved={() => { setDialogKind(null); load(); }}
        defaultRate={data?.default_rate || 100} expensesTotal={data?.expenses_total || 0} fmt={fmt} />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar registo?</AlertDialogTitle>
            <AlertDialogDescription>O valor volta ao dinheiro por dividir.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[hsl(var(--destructive))]" data-testid="confirm-delete-money-entry">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
